import React, {createContext, useContext, useState, useCallback, useEffect} from 'react';

const STORAGE_KEY_UMBRELLA = 'nofam.scope.umbrellaId';
const STORAGE_KEY_CHARITY = 'nofam.scope.charityId';
const STORAGE_KEY_UMBRELLA_EXTERNAL = 'nofam.scope.umbrellaExternalId';
const STORAGE_KEY_CHARITY_EXTERNAL = 'nofam.scope.charityExternalId';

const ScopeContext = createContext(null);

const readStored = () => {
    try {
        const u = localStorage.getItem(STORAGE_KEY_UMBRELLA);
        const c = localStorage.getItem(STORAGE_KEY_CHARITY);
        const ue = localStorage.getItem(STORAGE_KEY_UMBRELLA_EXTERNAL);
        const ce = localStorage.getItem(STORAGE_KEY_CHARITY_EXTERNAL);
        return {
            umbrellaId: u ? Number(u) : null,
            charityId: c ? Number(c) : null,
            umbrellaExternalId: ue || null,
            charityExternalId: ce || null,
        };
    } catch {
        return {umbrellaId: null, charityId: null, umbrellaExternalId: null, charityExternalId: null};
    }
};

const writeStored = (umbrellaId, charityId, umbrellaExternalId, charityExternalId) => {
    try {
        if (umbrellaId == null) localStorage.removeItem(STORAGE_KEY_UMBRELLA);
        else localStorage.setItem(STORAGE_KEY_UMBRELLA, String(umbrellaId));
        if (charityId == null) localStorage.removeItem(STORAGE_KEY_CHARITY);
        else localStorage.setItem(STORAGE_KEY_CHARITY, String(charityId));
        if (umbrellaExternalId == null) localStorage.removeItem(STORAGE_KEY_UMBRELLA_EXTERNAL);
        else localStorage.setItem(STORAGE_KEY_UMBRELLA_EXTERNAL, String(umbrellaExternalId));
        if (charityExternalId == null) localStorage.removeItem(STORAGE_KEY_CHARITY_EXTERNAL);
        else localStorage.setItem(STORAGE_KEY_CHARITY_EXTERNAL, String(charityExternalId));
    } catch {
        /* storage unavailable */
    }
};

export const ScopeProvider = ({children}) => {
    const [scope, setScopeState] = useState(readStored);

    const setScope = useCallback((next) => {
        setScopeState((prev) => {
            const merged = {...prev, ...next};
            if (merged.umbrellaId == null) {
                merged.charityId = null;
                merged.umbrellaExternalId = null;
                merged.charityExternalId = null;
            }
            writeStored(merged.umbrellaId, merged.charityId, merged.umbrellaExternalId, merged.charityExternalId);
            return merged;
        });
    }, []);

    const setUmbrella = useCallback((umbrellaId, umbrellaExternalId = null) => {
        setScopeState((prev) => {
            const umbrellaChanged = umbrellaId !== prev.umbrellaId;
            const next = {
                umbrellaId,
                umbrellaExternalId,
                charityId: umbrellaChanged ? null : prev.charityId,
                charityExternalId: umbrellaChanged ? null : prev.charityExternalId,
            };
            writeStored(next.umbrellaId, next.charityId, next.umbrellaExternalId, next.charityExternalId);
            return next;
        });
    }, []);

    const setCharity = useCallback((charityId, charityExternalId = null) => {
        setScopeState((prev) => {
            const next = {...prev, charityId, charityExternalId};
            writeStored(next.umbrellaId, next.charityId, next.umbrellaExternalId, next.charityExternalId);
            return next;
        });
    }, []);

    const clearScope = useCallback(() => {
        writeStored(null, null, null, null);
        setScopeState({umbrellaId: null, charityId: null, umbrellaExternalId: null, charityExternalId: null});
    }, []);

    useEffect(() => {
        const onStorage = (e) => {
            if (
                e.key !== STORAGE_KEY_UMBRELLA
                && e.key !== STORAGE_KEY_CHARITY
                && e.key !== STORAGE_KEY_UMBRELLA_EXTERNAL
                && e.key !== STORAGE_KEY_CHARITY_EXTERNAL
            ) return;
            setScopeState(readStored());
        };
        window.addEventListener('storage', onStorage);
        return () => window.removeEventListener('storage', onStorage);
    }, []);

    const value = {
        umbrellaId: scope.umbrellaId,
        charityId: scope.charityId,
        umbrellaExternalId: scope.umbrellaExternalId,
        charityExternalId: scope.charityExternalId,
        setScope,
        setUmbrella,
        setCharity,
        clearScope,
    };

    return <ScopeContext.Provider value={value}>{children}</ScopeContext.Provider>;
};

export const useScope = () => {
    const ctx = useContext(ScopeContext);
    if (!ctx) {
        return {
            umbrellaId: null,
            charityId: null,
            umbrellaExternalId: null,
            charityExternalId: null,
            setScope: () => {},
            setUmbrella: () => {},
            setCharity: () => {},
            clearScope: () => {},
        };
    }
    return ctx;
};
